import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Alert,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Mail as MailIcon,
} from "@mui/icons-material";
import axios from "axios";

const NewsletterSubscribers = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [selectedSubscriber, setSelectedSubscriber] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [searchEmail, setSearchEmail] = useState("");
  const [sortBy, setSortBy] = useState("newest");

  const API_URL =
    process.env.REACT_APP_API_URL || "https://13.205.31.119:5000/api";

  const token = localStorage.getItem("token");

  const fetchSubscribers = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await axios.get(`${API_URL}/newsletter/subscribers`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      let data = response.data.data || [];

      // Apply search filter
      if (searchEmail) {
        data = data.filter((sub) =>
          sub.email.toLowerCase().includes(searchEmail.toLowerCase())
        );
      }

      // Apply sorting
      if (sortBy === "newest") {
        data.sort(
          (a, b) => new Date(b.subscribedAt) - new Date(a.subscribedAt)
        );
      } else if (sortBy === "oldest") {
        data.sort(
          (a, b) => new Date(a.subscribedAt) - new Date(b.subscribedAt)
        );
      } else if (sortBy === "name") {
        data.sort((a, b) => a.email.localeCompare(b.email));
      }

      setSubscribers(data);
    } catch (err) {
      console.error("Error fetching subscribers:", err);
      setError(
        "Failed to load subscribers. Make sure the backend is running."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubscribers();
  }, []);

  useEffect(() => {
    const filtered = subscribers.filter((sub) =>
      sub.email.toLowerCase().includes(searchEmail.toLowerCase())
    );
    // Re-sort if needed
    if (sortBy === "newest") {
      filtered.sort(
        (a, b) => new Date(b.subscribedAt) - new Date(a.subscribedAt)
      );
    } else if (sortBy === "oldest") {
      filtered.sort(
        (a, b) => new Date(a.subscribedAt) - new Date(b.subscribedAt)
      );
    }
  }, [searchEmail, sortBy]);

  const handleDeleteClick = (subscriber) => {
    setSelectedSubscriber(subscriber);
    setOpenDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await axios.delete(
        `${API_URL}/newsletter/subscribers/${selectedSubscriber.email}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setSubscribers(
        subscribers.filter((sub) => sub.email !== selectedSubscriber.email)
      );
      setSuccess(`Subscriber ${selectedSubscriber.email} deleted successfully`);
      setTimeout(() => setSuccess(""), 3000);
      setOpenDeleteDialog(false);
      setSelectedSubscriber(null);
    } catch (err) {
      console.error("Error deleting subscriber:", err);
      setError("Failed to delete subscriber");
    }
  };

  const handleExportCSV = () => {
    if (subscribers.length === 0) {
      setError("No subscribers to export");
      return;
    }

    const csvContent = [
      ["Email", "Subscribed Date"],
      ...subscribers.map((sub) => [
        sub.email,
        new Date(sub.subscribedAt).toLocaleString(),
      ]),
    ]
      .map((row) => row.map((cell) => `"${cell}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `newsletter-subscribers-${new Date().toISOString().split("T")[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);

    setSuccess("CSV exported successfully");
    setTimeout(() => setSuccess(""), 3000);
  };

  return (
    <Box sx={{ p: 3, maxWidth: "1200px", mx: "auto" }}>
      {/* Header */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography
            variant="h4"
            sx={{ mb: 1, fontWeight: 600, color: "#1976d2" }}
          >
            📧 Newsletter Subscribers
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Manage and view all newsletter subscriptions
          </Typography>
        </CardContent>
      </Card>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}

      {/* Controls */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr",
              gap: 2,
              mb: 2,
            }}
          >
            {/* Search */}
            <TextField
              label="Search by email"
              variant="outlined"
              size="small"
              value={searchEmail}
              onChange={(e) => setSearchEmail(e.target.value)}
              placeholder="user@example.com"
              fullWidth
            />

            {/* Sort */}
            <FormControl size="small" fullWidth>
              <InputLabel>Sort By</InputLabel>
              <Select
                value={sortBy}
                label="Sort By"
                onChange={(e) => setSortBy(e.target.value)}
              >
                <MenuItem value="newest">Newest First</MenuItem>
                <MenuItem value="oldest">Oldest First</MenuItem>
                <MenuItem value="name">Email (A-Z)</MenuItem>
              </Select>
            </FormControl>

            {/* Actions */}
            <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={fetchSubscribers}
                size="small"
              >
                Refresh
              </Button>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleExportCSV}
                size="small"
                color="success"
              >
                Export CSV
              </Button>
            </Box>
          </Box>

          {/* Stats */}
          <Box sx={{ display: "flex", gap: 2, justifyContent: "space-between" }}>
            <Typography variant="body2" color="textSecondary">
              <strong>{subscribers.length}</strong> total subscribers
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Last updated: {new Date().toLocaleTimeString()}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Table */}
      <TableContainer component={Paper}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        ) : subscribers.length === 0 ? (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <MailIcon
              sx={{
                fontSize: 48,
                color: "#ccc",
                mb: 1,
              }}
            />
            <Typography color="textSecondary">
              {searchEmail ? "No subscribers found matching your search" : "No subscribers yet"}
            </Typography>
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                <TableCell sx={{ fontWeight: 600 }}>Email Address</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Subscribed Date</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="center">
                  Status
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="center">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {subscribers.map((subscriber, index) => (
                <TableRow
                  key={subscriber._id || index}
                  sx={{
                    "&:hover": { backgroundColor: "#f9f9f9" },
                    borderBottom: "1px solid #eee",
                  }}
                >
                  <TableCell>{subscriber.email}</TableCell>
                  <TableCell>
                    {new Date(subscriber.subscribedAt).toLocaleString()}
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={subscriber.isActive ? "Active" : "Inactive"}
                      color={subscriber.isActive ? "success" : "warning"}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeleteClick(subscriber)}
                      title="Delete subscriber"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Delete Subscriber</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete{" "}
            <strong>{selectedSubscriber?.email}</strong> from the newsletter?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NewsletterSubscribers;
